import React, {useCallback, useEffect, useState} from "react";
import {CircularProgress, MenuItem, Select, Tooltip, Typography} from "@mui/material";
import {v4 as uuidv4} from "uuid";
import AxiosFoundationAPI from "../../util/AxiosFoundationAPI";
import {SessionContext} from "../../util/SessionContext";
import {Grey5, Grey6, Grey17} from "../../theme/styles";

// The access level a share confers on ONE record: a picker on each Position and User Group share row.
//
// Replaces the old Capabilities dialog, which listed every capability in the account -- 53 in Peak, "Full
// access" sixteen times with no object type beside it -- as checkboxes you could tick several of.
//
// The server answers everything this shows (membership_capabilities?View=levels, backed by
// cres.utils.share_access.describe_levels): the levels for the record's own type, which of them THIS
// caller may grant -- never more than they hold on the record -- the level the share holds now, and
// whether the caller may manage the share at all. A level the caller may not grant is not offered, and a
// caller who may not manage the share sees the level as text. The writes refuse the same things, so this
// is courtesy, not the boundary.
//
// "Role default" means no level: the share makes the record reachable and its members act with their own
// roles. A level REPLACES their roles on this record, in both directions -- decided with Ed, 2026-09-16.

const GRANTS_URL = "t1/v1/membership_capabilities";
const GRANT_IDOC = "v1.MembershipCapability-001";
const ROLE_DEFAULT = "__role_default__";

// Most-to-least access, as the t6 Roles page orders them. Capability rows carry no ordinal.
const LEVEL_ORDER = ["Full access", "Editing access", "Operate access", "Read data values", "Read only", "View page access"];

type Level = {USID: string; Name: string; Description?: string; MayGrant: boolean};
type Described = {
    LevelBearing: boolean;
    MayManage: boolean;
    Current: {GrantUSID: string; CapabilityUSID: string; Name: string | null; ForThisType: boolean} | null;
    Levels: Level[];
};

function personaHeaders() {
    const visibility: any = new SessionContext().getActiveVisibility() || {};
    return {"visibility-mode": visibility.Mode ?? "", "visibility-value": visibility.Value ?? ""};
}

function rank(name: string) {
    const index = LEVEL_ORDER.indexOf(name);
    return index === -1 ? LEVEL_ORDER.length : index;
}

type Props = {
    membershipUSID: string;
    editable: boolean;       // false on a frozen record: the level is shown, never offered
};

export default function ShareAccessLevel({membershipUSID, editable}: Props) {
    const [described, setDescribed] = useState<Described | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // useCallback so the effect below can depend on it HONESTLY. The previous version carried an
    // eslint-disable comment inside the arrow body, where it suppressed the next LINE rather than
    // the dependency array -- so the rule fired anyway and the build has been failing since
    // 2026-09-21. Naming the real dependency is the fix; the comment was never doing anything.
    const load = useCallback(() => {
        return AxiosFoundationAPI({method: "GET", url: GRANTS_URL, params: {MembershipUSID: membershipUSID, View: "levels"},
                          headers: personaHeaders()})
            .then((response: any) => setDescribed(response.data))
            .catch(() => setDescribed({LevelBearing: false, MayManage: false, Current: null, Levels: []}));
    }, [membershipUSID]);

    useEffect(() => { load(); }, [load]);

    if (described === null) return <CircularProgress size={16} style={{margin: "8px"}}/>;
    if (!described.LevelBearing) return null;

    const current = described.Current?.CapabilityUSID ?? ROLE_DEFAULT;
    const label = described.Current ? (described.Current.Name ?? "Unknown level") : "Role default";
    const offered = described.Levels.filter(level => level.MayGrant).sort((a, b) => rank(a.Name) - rank(b.Name));
    // The current level stays selectable-looking even when this caller could not grant it themselves.
    const currentOffered = current === ROLE_DEFAULT || offered.some(level => level.USID === current);

    async function choose(value: string) {
        if (value === current || busy) return;
        setBusy(true);
        setError(null);
        try {
            if (value === ROLE_DEFAULT) {
                await AxiosFoundationAPI({method: "DELETE", url: GRANTS_URL,
                                 headers: {"idoc-request": GRANT_IDOC, ...personaHeaders()},
                                 data: {PlatformMessage: {"-idoc": GRANT_IDOC, MembershipCapability: {USID: described!.Current!.GrantUSID}}}});
            } else {
                // One call: the server replaces whatever level the share held, so there is no moment in
                // which the share holds none and its members fall back to their whole role.
                await AxiosFoundationAPI({method: "POST", url: GRANTS_URL,
                                 headers: {"idoc-request": GRANT_IDOC, ...personaHeaders()},
                                 data: {PlatformMessage: {"-idoc": GRANT_IDOC, MembershipCapability: {
                                     USID: uuidv4(), MembershipUSID: membershipUSID, CapabilityUSID: value}}}});
            }
        } catch (e: any) {
            setError(e?.response?.data?.Cause ?? "The access level could not be changed.");
        } finally {
            await load();
            setBusy(false);
        }
    }

    const manageable = editable && described.MayManage;

    return (
        <div style={{display: "flex", flexDirection: "column", minWidth: 0}}>
            <Typography style={{fontSize: 11, color: Grey6, textTransform: "uppercase", letterSpacing: "0.05em"}}>Access on this record</Typography>
            {manageable ? (
                <Tooltip placement="left" title="Role default: members act with their own roles. A level replaces their roles on this record — it can narrow or raise what they may do, but never beyond what you hold.">
                    <Select value={current} onChange={event => choose(String(event.target.value))}
                            disabled={busy} size="small" variant="standard" disableUnderline
                            sx={{fontSize: 14, fontWeight: 500, color: Grey17, "& .MuiSelect-select": {py: "2px"}}}
                            renderValue={() => busy ? "Saving…" : label}>
                        <MenuItem value={ROLE_DEFAULT} sx={{fontSize: 14}}>Role default</MenuItem>
                        {!currentOffered ? <MenuItem value={current} disabled sx={{fontSize: 14}}>{label}</MenuItem> : null}
                        {offered.map(level => (
                            <MenuItem key={level.USID} value={level.USID} sx={{fontSize: 14}}>{level.Name}</MenuItem>
                        ))}
                    </Select>
                </Tooltip>
            ) : (
                <Typography style={{fontSize: 14, fontWeight: 500, color: Grey17}}>{label}</Typography>
            )}
            {error ? <Typography style={{fontSize: 12, color: "#C9820A", maxWidth: "260px"}}>{error}</Typography> : null}
            {!error && described.Current && !described.Current.ForThisType
                ? <Typography style={{fontSize: 12, color: Grey5}}>Not a level for this kind of record.</Typography> : null}
        </div>
    );
}
